import React from "react";
import "./Suspend.scss";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import rateAgreementService from "../../services/RateAgreementService";
import { NextPayment, RateAgreementInfo } from "../../types/RateAgreementInfo";
import { Trans, useTranslation } from "react-i18next";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";
import { VerifyUser } from "../../components/verifyUser/VerifyUser";
import { Dialog } from "spot-component-library/lib/components/basic/dialog/Dialog";
import { Debtor } from "../../types/Debtor";
import { Panel } from "spot-component-library/lib/components/basic/panel/Panel";
import { formattedDate } from "../../components/FormatDate";
import { PaymentType } from "../../types/PaymentType";

type SuspendProps = {
    debtNumber: string;
    activeRateAgreement: RateAgreementInfo;
    onSuspendResult: (nextPayment: NextPayment) => void;
};

export const Suspend = (props: SuspendProps) => {
    const { t } = useTranslation();
    const [verifiedDebtor, setVerifiedDebtor] = React.useState<Debtor | null>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [activeRateAgreement, setActiveRateAgreement] = React.useState<RateAgreementInfo>({});
    const [paymentToSuspend, setPaymentToSuspend] = React.useState<NextPayment>({});
    const [paymentAfterSuspend, setPaymentAfterSuspend] = React.useState<NextPayment>({});
    const [view, setView] = React.useState<"success" | "verify-user">("verify-user");
    const debtNumber = props.debtNumber;

    React.useEffect(() => {
        setActiveRateAgreement(props.activeRateAgreement);

        if (props.activeRateAgreement.nextRatePayments && props.activeRateAgreement.nextRatePayments.length >= 2) {
            setPaymentToSuspend(props.activeRateAgreement.nextRatePayments[0]);
            setPaymentAfterSuspend(props.activeRateAgreement.nextRatePayments[1]);
        }
    }, []);

    const submitSuspendRateAgreement = async () => {
        if (!verifiedDebtor || !activeRateAgreement.rateAgreementId) {
            setErrorMessage(t("APP_ROOT.ERROR.SERVER_MESSAGE_DEFAULT"));
            return;
        }

        try {
            await rateAgreementService.suspendRateAgreement(debtNumber, verifiedDebtor, activeRateAgreement.rateAgreementId, true, paymentToSuspend.consecutiveNumber);

            props.onSuspendResult(paymentAfterSuspend);
            setView("success");
        } catch (error) {
            switch ((error as Error).message) {
                case "404":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_404"));
                    break;
                case "422":
                    setErrorMessage(t("APP_ROOT.ERROR.MESSAGE_422"));
                    break;
                default: {
                    setErrorMessage(t("APP_ROOT.ERROR.SERVER_MESSAGE_DEFAULT"));
                    break;
                }
            }
        }
    };

    const isButtonEnabled = (): boolean => {
        return !!verifiedDebtor;
    };

    const closeDialog = async () => {
        const dialogs = document.getElementsByClassName("dialog") as HTMLCollection;
        for (let i = 0; i < dialogs.length; i++) {
            const dialog = dialogs[i] as HTMLElement;
            dialog.click();
        }
    };

    const mapToPaymentMessageKey = (key: string | undefined): string => {
        switch (key) {
            case PaymentType.ABO_DIRECTDEBIT:
                return "RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.SUCCESS_PAYMENT_DIRECTDEBIT";
            case PaymentType.ABO_BANKTRANSFER:
                return "RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.SUCCESS_PAYMENT_BANKTRANSFER";
            default:
                return "";
        }
    };

    return (
        <>
            <Dialog
                dialogStyle={{
                    maxWidth: "600px",
                    width: "100%",
                }}
                closeIcon={false}
                triggerComponent={
                    view !== "success" && (
                        <span className={"suspend-trigger-btn"} data-test="suspend-trigger">
                            <i className={"icon-rate-break"}></i>
                            {t("RATE_AGREEMENT_INFO_SNIPPET.PAUSE_BUTTON")}
                        </span>
                    )
                }>
                {view === "verify-user" && (
                    <>
                        <div className={"suspend-title"}>
                            <div>{t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.SUSPEND_DIALOG_TITLE")}</div>
                        </div>

                        <div className={"suspend-text"}>{t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.TEXT")}</div>

                        <Panel>
                            <VerifyUser debtNumber={debtNumber} onVerifyResult={(verifiedDebtor) => setVerifiedDebtor(verifiedDebtor)} />
                            <div className={"suspend-required-hint"}>{t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.REQUIRED")}</div>
                        </Panel>

                        {errorMessage && (
                            <>
                                <br />
                                <SPOTNotification id={"suspend-error"} text={errorMessage as string} active={true} type={"error"} />
                            </>
                        )}

                        <br />
                        <br />
                        <div className={"row-wrapper"}>
                            <SPOTButton
                                type="secondary"
                                label={t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.BUTTON_CANCEL")}
                                onClick={closeDialog}
                                isLoading={false}
                                dataTest={"close-suspend-btn"}
                            />
                            <SPOTButton
                                type="primary"
                                label={t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.BUTTON_CONFIRM")}
                                onClick={submitSuspendRateAgreement}
                                isLoading={false}
                                dataTest={"suspend-btn"}
                                disabled={!isButtonEnabled()}
                            />
                        </div>
                    </>
                )}

                {view === "success" && (
                    <>
                        <div className={"suspend-title"}>
                            <i className="icon-finish" />
                            <div style={{ paddingTop: 10 }}>{t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.SUCCESS_TITLE")}</div>
                        </div>

                        <div className={"suspend-text"}>
                            <Trans
                                i18nKey="RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.SUCCESS_TEXT"
                                values={{
                                    nextPaymentDate: formattedDate(paymentAfterSuspend.dueDate as string),
                                }}
                            />
                        </div>

                        <div className={"suspend-text"}>
                            <Trans
                                i18nKey={mapToPaymentMessageKey(activeRateAgreement.paymentMethod)}
                                values={{
                                    nextPaymentDate: formattedDate(paymentAfterSuspend.dueDate as string),
                                }}
                            />
                        </div>

                        <div className={"close-success-btn"}>
                            <SPOTButton
                                type="secondary"
                                label={t("RATE_AGREEMENT_INFO_SNIPPET.SUSPEND.BUTTON_CLOSE")}
                                onClick={closeDialog}
                                isLoading={false}
                                dataTest={"close-suspend-btn"}
                            />
                        </div>
                    </>
                )}
            </Dialog>
        </>
    );
};
